import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Crown, Gem, Gift, Mic, Drum, AudioWaveform, Gamepad2, Timer, Lock,
  Waves, Filter, Rewind, Play, Ghost, Sparkles, KeyRound, Trophy,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { GAME_META } from "@/components/medusa/ArcadeGames";

const GAMES = [
  { id: "piano", name: "I. Piano Scales", how: "Scales and crystals fall with the room's 138 BPM beat. Tap the lane — or keys D · F · J · K — the moment a tile crosses the gold line. Chain combos for multipliers. Five misses ends the run." },
  { id: "quiz", name: "II. Venom Drop", how: "Three seconds of a real Cold-Blooded Soundscape play — you have five seconds to name the track. Faster ears score bigger. Five rounds." },
  { id: "spiral", name: "III. Serpent Spiral", how: "The ritual scrambles tempo and pitch. Slide the BPM and semitone dials until your frequency matches the target resonance, then lock in — speed is points. Three rounds." },
  { id: "bass", name: "IV. Drop the Bass", how: "A playhead charges the 808. Slam SPACEBAR or tap the DROP button exactly when it crosses the gold zone. Ten drops — the zone shrinks and the speed rises every round." },
  { id: "memory", name: "V. Beat Builder", how: "Simon-says with stems. Watch the four pads glow in sequence — Drums, Bass, Melody, Vocals — then play the pattern back. It grows every round; one wrong pad ends the run." },
];

const Section = ({ id, kicker, title, children }) => (
  <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-14" data-testid={`guide-${id}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-400">{kicker}</p>
    <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1.5 mb-5">{title}</h2>
    {children}
  </section>
);

const Card = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-2xl border border-[#26262e] bg-[#0B0B10]/80 p-5" data-testid={testId}>
    <div className="flex items-center gap-2.5 mb-2">
      <Icon size={16} className="text-teal-300 shrink-0" />
      <p className="font-display text-white text-sm font-bold">{title}</p>
    </div>
    <div className="text-xs text-ice/55 leading-relaxed">{children}</div>
  </div>
);

export default function MedusaGuidePage() {
  useEffect(() => {
    document.title = "Medusa Room Visitor Guide | DGE: Cold-Blooded Records LLC™";
    return () => { document.title = "DGE: Cold-Blooded Records LLC™ | Official ASCAP® Member Label — Est. 2026"; };
  }, []);

  return (
    <main className="bg-obsidian text-ice min-h-screen" data-testid="medusa-guide-page">
      <Navbar />
      <div className="pt-[70px]">
        {/* hero */}
        <header className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-14 text-center">
          <img src="/medusa-logo.webp" alt="Cold-Blooded Records Medusa emblem" className="w-36 sm:w-48 mx-auto drop-shadow-[0_0_36px_rgba(236,72,153,0.45)]" data-testid="guide-logo" />
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400">Cold-Blooded Soundscapes™</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold text-white" data-testid="guide-title">The Visitor's Guide</h1>
          <p className="mt-4 text-sm sm:text-base text-ice/60 max-w-2xl mx-auto leading-relaxed">
            Everything inside the Medusa Room and the VIP Vault — the mixer, the arcade, the Snake Pit, the crystals, and the rewards — explained before you step through the gate.
          </p>
          <Link to="/medusa" className="mt-7 inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500 text-white font-bold text-sm px-9 py-4 rounded-full shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:shadow-[0_0_46px_rgba(236,72,153,0.7)] active:scale-95 transition-all" data-testid="guide-enter-cta">
            <Play size={16} /> Enter the Medusa Room
          </Link>
        </header>

        {/* entering */}
        <Section id="entering" kicker="First Steps" title="Entering the Room">
          <div className="grid sm:grid-cols-2 gap-3">
            <Card icon={KeyRound} title="The Gate" testId="guide-gate-card">
              The room opens with a single tap on Enter — that gesture wakes the sound engine (browsers require it). Prefer silence? Choose Enter Silently and nothing plays.
            </Card>
            <Card icon={Waves} title="Atmosphere & Mute" testId="guide-atmosphere-card">
              A generative dark pad and a sparse serpent rattle drift under everything. The Mute Atmosphere button (bottom-left) silences only the ambience — games and beats still sound.
            </Card>
            <Card icon={AudioWaveform} title="Gorgon Streak" testId="guide-streak-card">
              Visit the room on consecutive days and your Gorgon Streak grows — the chip near the top tracks your run. Miss a day and it resets to one.
            </Card>
            <Card icon={Sparkles} title="Nothing to Install" testId="guide-install-card">
              The whole room runs in your browser — synthesized audio, canvas visuals, no downloads. It also works inside the DGE app (PWA) on your phone.
            </Card>
          </div>
        </Section>

        {/* mixer */}
        <Section id="mixer" kicker="The Studio" title="Petrification Mixer & Venom FX">
          <div className="grid sm:grid-cols-2 gap-3">
            <Card icon={Drum} title="Wake the Stems" testId="guide-stems-card">
              Press Play the Beat to start a generative 138 BPM dark-trap loop. Four stem cards — Drums, Bass, Melody, Vox — sit in STONE (grey) or ALIVE (neon). Tap to petrify or revive each one and sculpt the mix live while the waveform breathes.
            </Card>
            <Card icon={Waves} title="Venom FX Pad" testId="guide-fx-card">
              Three performance effects hit the live mix: <span className="text-teal-300">Dungeon Reverb</span> (cathedral echo, toggle), <span className="text-yellow-300">Tape Stop</span> (one-shot pitch drain and recover), and <span className="text-pink-300">Low-Pass</span> (underwater muffled filter, toggle). Stack them however you like.
            </Card>
            <Card icon={Gift} title="Venom Meter Reward" testId="guide-venom-card">
              Keep the beat playing and the Venom Meter fills — about 75 seconds of listening. At 100% the room unlocks the SERPENT20 code (20% off any beat lease) plus a free beat download from the catalog.
            </Card>
            <Card icon={Timer} title="Petrified Countdown" testId="guide-countdown-card">
              One beat per day sits under the countdown. When the clock hits local midnight it turns to stone — greyed out and unbuyable. License it before time runs out; the label can also schedule specific drop days.
            </Card>
          </div>
        </Section>

        {/* snake pit */}
        <Section id="snakepit" kicker="A&R Pipeline" title="The Snake Pit & Ghost Verses">
          <div className="grid sm:grid-cols-2 gap-3">
            <Card icon={Mic} title="Cut a Vocal Idea" testId="guide-record-card">
              Press Record Idea, allow microphone access, and lay up to 30 seconds of vocals over the room's beat. Preview it, add your artist name and email, and Send to Label — it lands directly on the A&R desk as a talent submission.
            </Card>
            <Card icon={Ghost} title="Ghost Verses" testId="guide-ghost-card">
              The label hand-picks the best Snake Pit sends and features them as Ghost Verses — public auditions anyone in the room can press play on. Cut something undeniable and your voice could haunt the room.
            </Card>
          </div>
        </Section>

        {/* arcade */}
        <Section id="arcade" kicker="The Arcade Chamber" title="Five Trials, Five Leaderboards">
          <p className="text-sm text-ice/55 max-w-2xl mb-6 leading-relaxed">
            Every game has its own leaderboard and its own prize ladder. When your run ends, submit your player name (email recommended — that's how prizes arrive). Prize tiers per game:
          </p>
          <div className="space-y-3" data-testid="guide-games">
            {GAMES.map((g) => (
              <div key={g.id} className="rounded-2xl border bg-[#0B0B10]/80 p-5" style={{ borderColor: `${GAME_META[g.id].color}33` }} data-testid={`guide-game-${g.id}`}>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <Gamepad2 size={16} style={{ color: GAME_META[g.id].color }} />
                  <p className="font-display text-white text-sm font-bold">{g.name}</p>
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.12em] text-yellow-300/90">Prizes: {GAME_META[g.id].tiers}</span>
                </div>
                <p className="text-xs text-ice/55 leading-relaxed">{g.how}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-2xl border border-yellow-500/30 bg-yellow-950/10 p-5" data-testid="guide-prizes-note">
            <div className="flex items-start gap-3">
              <Trophy size={18} className="text-yellow-300 shrink-0 mt-0.5" />
              <p className="text-xs text-ice/60 leading-relaxed">
                Discount prizes arrive as unique one-time codes you enter at beat checkout. Free leases and sample kits are confirmed by the label over email. Every win appears on the board with a gift marker — top the board and the whole room sees it.
              </p>
            </div>
          </div>
        </Section>

        {/* vault */}
        <Section id="vault" kicker="Members Only" title="The Crystal Vault & VIP Membership">
          <div className="grid sm:grid-cols-2 gap-3">
            <Card icon={Gem} title="Crystal Resonance Puzzle" testId="guide-crystal-card">
              Three crystals — Amethyst, Obsidian, Emerald — guard the vault. Tap them in the resonant order (the riddle beside them is the key). A wrong sequence resets; the right one sweeps gold and opens the VIP Vault.
            </Card>
            <Card icon={Lock} title="What's in the VIP Vault" testId="guide-vault-card">
              Members-only drops: exclusive links, notes, and material the label doesn't post publicly. The vault lives both inside the Medusa Room and at its own entrance on the VIP Vault page.
            </Card>
            <Card icon={Crown} title="Membership Plans" testId="guide-plans-card">
              Monthly is $9.99/month with a 7-day free trial — you are not charged until the trial ends, and the label emails a reminder two days before. Yearly is $99/year, the better deal. Sign in with Google, pick a plan, and checkout runs securely through Stripe.
            </Card>
            <Card icon={Play} title="Beats & Licensing" testId="guide-beats-card">
              The room's catalog groups Cold-Blooded Soundscapes by mood with BPM and key tags. Preview any beat, then Secure License opens the standard licensing tiers — MP3 Lease $29, WAV Lease $59, Unlimited $149, Exclusive $499 — delivered instantly after checkout.
            </Card>
          </div>
          <div className="mt-8 text-center">
            <Link to="/medusa" className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-9 py-4 rounded-full hover:shadow-[0_0_28px_rgba(245,184,76,0.55)] active:scale-95 transition-all" data-testid="guide-bottom-cta">
              <Gem size={15} /> Step Into the Medusa Room
            </Link>
            <p className="mt-4 text-[11px] text-ice/40">
              By entering the room you agree to the <Link to="/medusa-terms" className="text-teal-300 hover:text-teal-200 transition-colors" data-testid="guide-terms-link">Medusa Room Terms &amp; House Rules</Link>.
            </p>
          </div>
        </Section>
      </div>
      <Footer />
    </main>
  );
}
